import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import { REPEATABLE_SECTION_STATE } from "../../../common/utils/enums";
import { momentDateDisplayFormat } from '../../../common/utils/formats';
import FormSection from '../../../src/data/FormSection';

export const applyClientCalculatedData = sections => {
    const clientDataCache = {};

    return sections
        .reduce((acc, section) => {
            if (section.is_repeatable) {
                let { currentIndex = -1 } = clientDataCache[section.id] || {};

                currentIndex += 1;

                const isEmpty = !section.questions.find(question => !!question.data.id) && !section?.clientData?.isNew;

                section.clientData = {
                    ...section.clientData,
                    repeatIndex: currentIndex,
                    isEmpty
                };

                clientDataCache[section.id] = {
                    ...clientDataCache[section.id],
                    currentIndex,
                };
            }

            acc.push(section);

            return acc;
        }, [])
        .reduce((acc, curr) => {
            const { currentIndex } = clientDataCache[curr.id] || {};
            if (curr.is_repeatable) {
                curr.clientData = {
                    ...curr.clientData,
                    // We will use it to decide to show "Delete" button for the section.
                    // If only 1 section is exists, then we will hide the delete button
                    allSiblingsCount: currentIndex + 1,
                }
            }

            acc.push(curr);

            return acc;
        }, []);
}

// @todo: refactor this code and use splice method if possible
export const changeRepeatableSectionsState = (allSiblingSections, targetSection, state) =>
{
    const targetSectionIndex = allSiblingSections.indexOf(targetSection);

    allSiblingSections.forEach((section, index) => {
        const isTargetSection = index === targetSectionIndex;

        section.clientData = {
            ...section.clientData,
            isUpdate: isTargetSection && state === REPEATABLE_SECTION_STATE.Update,
            isCreate: isTargetSection && state === REPEATABLE_SECTION_STATE.Create,
        };

        allSiblingSections.splice(index, 1, section);

        return section;
    });
}

export const setSortIndex = (allSections, referenceSection) => {
    const currentSortIndex = allSections
        .filter(section => section.parent_id === referenceSection.parent_id || (!section.parent_id && !referenceSection.parent_id))
        .reduce((acc, curr) => {
            if (curr.is_repeatable && curr.section_sort_index > 0) {
                acc = Math.max(acc, curr.section_sort_index);
            }

            return acc;
        }, 0);

    if (!referenceSection.section_sort_index) {
        referenceSection.section_sort_index = currentSortIndex + 1;
    }

    allSections.splice(allSections.indexOf(referenceSection), 1, referenceSection);
}

export const addNewSection = (allSections, referenceSection) => {
    // copy the main section with owned questions.
    const clonedSection = new FormSection(referenceSection.getData(true));

    // reset the questions' data id and return_value fields
    for (let i = 0, l = clonedSection?.questions?.length; i < l; i += 1) {
        const question = clonedSection.questions[i];
        question.data.id = null;
        question.data.return_value = question.default_value;
        question.data.docs.length = 0;
        question.data.notes = null;
    }

    // sectiona bir form_ques_data_group_id ver (subsectionlarda ordan gidecek) gosterimde kullanacagiz
    clonedSection.form_ques_data_group_id = uuidv4();
    clonedSection.clientData = {
        ...clonedSection.clientData,
        isNew: true,
    };

    // if there is only one empty section in list, then do not create new clone. Instead, use the current one.
    if (referenceSection.clientData.allSiblingsCount === 1 && referenceSection.clientData.isEmpty) {
        allSections.splice(allSections.indexOf(referenceSection), 1, clonedSection);
    } else {
        allSections.push(clonedSection);
    }

    setSortIndex(allSections, clonedSection);

    changeRepeatableSectionsState(
        allSections,
        clonedSection,
        REPEATABLE_SECTION_STATE.Create
    );
}

export const deleteSection = (allSections, targetSection) => {
    const isReset = targetSection.clientData.allSiblingsCount === 1;

    // do not delete the section if it is the only one. Instead, reset all of its questions data
    if (isReset) {
        targetSection.form_ques_data_group_id = null;
        targetSection.clientData = {
            ...targetSection.clientData,
            isNew: false,
            isEdit: false,
            isCreate: false,
        };

        targetSection.questions = targetSection.questions.map((question) => {
            question.data = {
                ...question.data,
                return_value: question.default_value,
                id: null,
            };

            return question;
        });

        // not sure if I really need this. check it when you have time
        allSections.splice(allSections.indexOf(targetSection), 1, targetSection);
    } else {
        allSections.splice(allSections.indexOf(targetSection), 1);
    }
}

export const startEditingSection = (allSections, targetSection) => {
    // We should delete the section if it is newly created but not saved
    const sectionToDelete = allSections.find(({ clientData }) => clientData?.isCreate);

    if (sectionToDelete) {
        deleteSection(allSections, sectionToDelete);
    }

    changeRepeatableSectionsState(
        allSections,
        targetSection,
        REPEATABLE_SECTION_STATE.Update
    );
}

export const cancelEditingSection = (allSections, targetSection, initialSection) => {
    if (targetSection?.clientData.isCreate) {
        deleteSection(allSections, targetSection);
        return;
    }

    allSections.splice(
        allSections.indexOf(targetSection),
        1,
        initialSection
    );

    changeRepeatableSectionsState(
        allSections,
        targetSection,
        REPEATABLE_SECTION_STATE.View
    );
}

export const saveSection = (allSections, targetSection) => {
    changeRepeatableSectionsState(
        allSections,
        targetSection,
        REPEATABLE_SECTION_STATE.View
    );
}

export const getQuestionValueLabel = question => {
    const { data: { return_value }, control_data = [] } = question;

    switch(true) {
        case question.isLabel:
        case question.isTextArea:
        case question.isTextBox:
        case question.isNumber:
        case question.isMoney:
        case question.isCoordinate:
        case question.isYesNo:
        case question.isTime:
        {
            return return_value;
        }

        case question.isDate: {
            if (return_value) {
                return moment(return_value).format(momentDateDisplayFormat);
            }

            return return_value;
        }

        case question.isNCBUNID:
        case question.isComboBoxAsCombo:
        case question.isComboBox: {
            const controlData = control_data.find(data => data.cd_key === return_value)
            return controlData ? (controlData.is_other_value ? question.data.other_value : controlData.cd_value) : question.data.display_value;
        }

        case question.isCheckBoxList: {
            if (!return_value) {
                return return_value;
            }

            const valuesMap = return_value.split(',').reduce((acc, curr) => {
                acc[curr] = true;
                return acc;
            }, {});

            return control_data.filter(data => valuesMap[data.cd_key]).map(data => data.cd_value).join(', ');
        }

        case question.isImage: {
            // @todo: return link
            return 'Image';
        }

        case question.isVideo: {
            // @todo: return link
            return 'Video';
        }

        case question.isAudio: {
            // @todo: return link
            return 'Audio';
        }

        case question.isSignature: {
            // @todo: show signature
            return 'Signature';
        }

        default:
            return return_value;
    }
};
